import { useState, type FC } from "react";
import { UserPlus, Send } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { FieldError } from "@/shared/components/ui/FieldError";
import { isValidEmail } from "@/shared/utils/validators";

interface InviteCardProps {
  contactName: string;
  contactEmail: string | null;
  isInvited: boolean;
  deceasedFirstName: string;
  pendingInvitation?: { id: number; email: string } | null;
  onInvite?: (email: string, isAttorney?: boolean) => Promise<void>;
  onResendInvite?: (invitationId: number, email: string) => Promise<void>;
  onCancelInvite?: (invitationId: number) => Promise<void>;
  isInviting?: boolean;
}

export const InviteCard: FC<InviteCardProps> = ({
  contactName,
  contactEmail,
  isInvited,
  deceasedFirstName,
  pendingInvitation,
  onInvite,
  onResendInvite,
  onCancelInvite,
  isInviting = false,
}) => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const description = `We'll email them an invitation. They'll be able to access ${deceasedFirstName}'s estate record.`;

  if (pendingInvitation) {
    return (
      <div className="bg-yellow-50 border border-alert rounded-lg flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">
            We invited {contactName} at {pendingInvitation.email}
          </p>
          <p className="text-sm text-secondary">{description}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onResendInvite?.(pendingInvitation.id, pendingInvitation.email)}
            disabled={isInviting}
          >
            <Send className="size-4 mr-2" />
            Resend invite
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onCancelInvite?.(pendingInvitation.id)}
            disabled={isInviting}
          >
            Cancel invite
          </Button>
        </div>
      </div>
    );
  }

  if (contactEmail && !isInvited) {
    return (
      <div className="bg-brand-subtle border border-brand rounded-lg flex gap-3 items-center p-4">
        <div className="flex-1 flex flex-col gap-1">
          <p className="text-sm font-medium">
            Invite {contactName} at {contactEmail}?
          </p>
          <p className="text-sm text-secondary">{description}</p>
        </div>
        <Button
          variant="brand"
          className="shrink-0"
          onClick={() => onInvite?.(contactEmail)}
          disabled={isInviting}
        >
          <UserPlus className="size-4 mr-2" />
          Invite
        </Button>
      </div>
    );
  }

  if (!contactEmail && !isInvited) {
    return (
      <div className="bg-brand-subtle border border-brand rounded-lg flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">Invite {contactName} to Elayne?</p>
          <p className="text-sm text-secondary">{description}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">Email</Label>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                if (emailError) setEmailError("");
              }}
              className={`flex-1 ${emailError ? "border-destructive" : ""}`}
            />
            <Button
              variant="brand"
              className="shrink-0"
              onClick={() => {
                const trimmed = inviteEmail.trim();
                if (!trimmed || !isValidEmail(trimmed)) {
                  setEmailError("Enter a valid email address");
                  return;
                }
                setEmailError("");
                onInvite?.(trimmed);
              }}
              disabled={isInviting || !inviteEmail.trim()}
            >
              <UserPlus className="size-4 mr-2" />
              Invite
            </Button>
          </div>
          <FieldError message={emailError} />
        </div>
      </div>
    );
  }

  return null;
};
